import { useCallback, useContext, useMemo, useState, useEffect, useRef } from "react";
//internal-deps
import { CheckoutActionTypes, CheckoutContext } from "@swiggy-private/checkout-core";
import { IAppliedCouponCodes, ICouponCard } from "@swiggy-private/cart-types";
//contexts
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
//helpers
import { isEmpty } from "@swiggy-private/js-utils";
import {
    getUpdatedAppliedCoupons,
    getOtherBusinessLineCouponsToDeselect,
    areAllCouponsNotApplicable,
} from "@cart-client/helpers/CouponListing";
import { buildGiftingCouponAnalyticsPayload } from "@cart-client/helpers/Coupon";
//interfaces
import { CouponCardImpressionPayload } from "@swiggy-private/core-types";
//analytics
import { GiftingCouponAnalytics } from "@cart-client/components/coupon-listing/types";

export const useGiftingCouponLogic = (
    coupons: ICouponCard[] | null,
    giftingAnalytics: GiftingCouponAnalytics,
    appliedGiftingCoupons?: IAppliedCouponCodes[],
) => {
    const userCouponSelectionUpdateRef = useRef<boolean>(false);
    const couponSelectionInitRef = useRef<boolean>(false);
    const { setCheckoutState } = useContext(CheckoutContext);
    const { syncApplyCouponCart } = useContext(UpdateCartContext);

    const [couponSelections, setCouponSelections] = useState<Map<string, boolean>>(new Map());
    const isCouponAvailable = useMemo(() => areAllCouponsNotApplicable(coupons), [coupons]);

    const [couponApplied, setCouponApplied] = useState<boolean>(!isEmpty(appliedGiftingCoupons));

    const updatedAppliedCouponCodes = useMemo(
        () => getUpdatedAppliedCoupons(coupons, couponSelections),
        [coupons, couponSelections],
    );

    const isFooterDisabled = useMemo(
        () => isEmpty(updatedAppliedCouponCodes) || !isCouponAvailable,
        [updatedAppliedCouponCodes, isCouponAvailable],
    );

    useEffect(() => {
        // If user has made selections, set to false
        if (couponSelections.size > 0 && userCouponSelectionUpdateRef.current) {
            setCouponApplied(false);
        }
    }, [couponSelections]);

    const navigateToGiftingCart = useCallback(() => {
        setCheckoutState({
            type: CheckoutActionTypes.SHOW_GIFTING_CART,
            payload: {
                skipCartReload: true,
            },
        });
    }, [setCheckoutState]);

    const updateGiftingCouponHandler = useCallback(
        (payload: CouponCardImpressionPayload) => {
            if (couponApplied && isEmpty(payload.couponCode)) {
                navigateToGiftingCart();
            } else if (!isEmpty(updatedAppliedCouponCodes) || !isEmpty(payload.couponCode)) {
                if (!isEmpty(payload.couponCode)) {
                    giftingAnalytics.handleApplyInputBoxClick(payload.couponCode || "");
                }
                syncApplyCouponCart?.({
                    appliedCouponCodes: updatedAppliedCouponCodes,
                    couponCode: payload.couponCode,
                    isCouponListingFlow: true,
                });
            }
        },
        [couponApplied, giftingAnalytics, navigateToGiftingCart, syncApplyCouponCart, updatedAppliedCouponCodes],
    );

    const onCouponCheckboxClick = useCallback(
        (couponId: string, businessLine?: string) => {
            userCouponSelectionUpdateRef.current = true;
            setCouponSelections((prev) => {
                const newSelections = new Map(prev);
                const currentState = newSelections.get(couponId) || false;
                const isSelecting = !currentState;

                // Find the coupon data for analytics
                const coupon = coupons?.flatMap((card) => card.coupons).find((c) => c?.id === couponId);

                if (!isEmpty(coupon)) {
                    const isApplied =
                        appliedGiftingCoupons?.some(
                            (appliedCoupon) =>
                                appliedCoupon.code === coupon.title &&
                                appliedCoupon.businessLine === coupon.businessLine,
                        ) || false;

                    const payload = buildGiftingCouponAnalyticsPayload({ couponId, coupon, isSelecting, isApplied });

                    if (isSelecting) {
                        giftingAnalytics.handleSelectCouponCheckboxClick(payload);
                    } else {
                        giftingAnalytics.handleDeselectCouponCheckboxClick(payload);
                    }
                }

                if (isSelecting && businessLine) {
                    const couponsToDeselect = getOtherBusinessLineCouponsToDeselect(coupons, businessLine, couponId);
                    couponsToDeselect.forEach((value, key) => {
                        newSelections.set(key, value);
                    });
                }

                newSelections.set(couponId, isSelecting);
                return newSelections;
            });
        },
        [coupons, giftingAnalytics, appliedGiftingCoupons],
    );

    const initCouponSelectionMap = useCallback((coupons: ICouponCard[]) => {
        const selectionMap = new Map();
        const flatCoupons = coupons?.flatMap((card) => card.coupons);
        flatCoupons?.forEach((coupon) => {
            if (!isEmpty(coupon?.id)) {
                selectionMap.set(coupon?.id, coupon?.isSelected ?? false);
            }
        });
        setCouponSelections(selectionMap);
    }, []);

    // hook is mounted when coupon listing loads, need to run once only when coupons are loaded
    useEffect(() => {
        if (!isEmpty(coupons) && !couponSelectionInitRef.current) {
            initCouponSelectionMap(coupons);
            couponSelectionInitRef.current = true;
        }
    }, [coupons, initCouponSelectionMap]);

    return {
        updatedAppliedCouponCodes,
        isFooterDisabled,
        couponApplied,
        couponSelections,
        isCouponAvailable,
        updateGiftingCouponHandler,
        onCouponCheckboxClick,
        navigateToGiftingCart,
    };
};
